"use client"
import { Popup } from "react-map-gl/mapbox";
import Asset from "@/components/shared/Asset";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { Badge, Button, Form, Modal, Ratio, Spinner } from "react-bootstrap";
import MapLink from "@/components/shared/MapLink";
import UralityImage from "@/components/shared/UralityImage";
import CollapseText from "@/components/shared/CollapseText";
import SocialLinks from "@/components/social/SocialLinks";
import useUtils from "@/components/shared/useUtils";
import { useMediaQuery } from "react-responsive";
import useApi from "@/components/api/useApi";

import "@/components/assetmanagement/PinLayerPointPopup.css";
import ActivityLogEntry from "@/components/shared/ActivityLogEntry";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";


const PinLayerPointPopup = ({
  pinLayerPoint,
  ctas,
  openIncidents,
  name,
  assetGroup,
  registry,
  onViewIncident,
  editable = false,
  onPointUpdated
}) => {
  const utils = useUtils();
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });
  const api = useApi();

  const [expanded, setExpanded] = useState(false);
  const [expandedIncidents, setExpandedIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  // Inline field edit state
  const [activeEdit, setActiveEdit] = useState(null); // { fieldName } | null
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (activeEdit && inputRef.current) inputRef.current.focus();
  }, [activeEdit]);

  const hasOpenIncidents = openIncidents && openIncidents.length > 0;

  useEffect(() => {
    if (expanded && name && assetGroup?.id && pinLayerPoint?.id) {
      setLoadingIncidents(true);
      api
        .listIncidents(name, assetGroup.id, {
          pointId: pinLayerPoint.id,
          status: "OPEN",
          max: 5
        })
        .then(data => {
          setExpandedIncidents(Array.isArray(data) ? data : data?.items || []);
          setLoadingIncidents(false);
        })
        .catch(() => setLoadingIncidents(false));
    }
  }, [expanded, name, assetGroup?.id, pinLayerPoint?.id]);

  const hasCustomFields = object => {
    if (assetGroup?.fields?.length > 0) return true;
    return (
      object &&
      ((object["stringCustomFields"] &&
        Object.keys(object["stringCustomFields"]).length > 0) ||
        (object["numberCustomFields"] &&
          Object.keys(object["numberCustomFields"]).length > 0) ||
        (object["booleanCustomFields"] &&
          Object.keys(object["booleanCustomFields"]).length > 0))
    );
  };

  const getFieldValue = field => {
    if (field.type === "STRING")    return pinLayerPoint.stringCustomFields?.[field.name] ?? "";
    if (field.type === "NUMBER") {
      const v = pinLayerPoint.numberCustomFields?.[field.name];
      return v == null ? "" : String(v);
    }
    if (field.type === "BOOLEAN")   return pinLayerPoint.booleanCustomFields?.[field.name] ?? false;
    if (field.type === "READ_ONLY") return pinLayerPoint.readOnlyFields?.[field.name] ?? "";
    return "";
  };

  const buildUpdatedPoint = (field, newValue) => {
    const updated = {
      ...pinLayerPoint,
      stringCustomFields:  { ...(pinLayerPoint.stringCustomFields  || {}) },
      numberCustomFields:  { ...(pinLayerPoint.numberCustomFields  || {}) },
      booleanCustomFields: { ...(pinLayerPoint.booleanCustomFields || {}) },
      readOnlyFields:      { ...(pinLayerPoint.readOnlyFields      || {}) }
    };
    if (field.type === "STRING")  updated.stringCustomFields[field.name]  = newValue;
    if (field.type === "NUMBER")  updated.numberCustomFields[field.name]  = newValue === "" ? null : Number(newValue);
    if (field.type === "BOOLEAN") updated.booleanCustomFields[field.name] = newValue;
    return updated;
  };

  const commitEdit = async field => {
    if (!activeEdit) return;
    const updated = buildUpdatedPoint(field, editValue);
    setActiveEdit(null);
    setEditValue("");
    try {
      const saved = await api.updatePinLayerPoint(name, assetGroup.id, pinLayerPoint.id, updated);
      if (onPointUpdated) onPointUpdated(saved ?? updated);
    } catch (_) {
      window.flash_messages?.addMessage({ id: "popup-field-err", text: "Failed to save field value.", type: "error" });
    }
  };

  const commitBoolean = async (field, newValue) => {
    const updated = buildUpdatedPoint(field, newValue);
    try {
      const saved = await api.updatePinLayerPoint(name, assetGroup.id, pinLayerPoint.id, updated);
      if (onPointUpdated) onPointUpdated(saved ?? updated);
    } catch (_) {
      window.flash_messages?.addMessage({ id: "popup-field-err", text: "Failed to save field value.", type: "error" });
    }
  };

  const startEdit = field => {
    setActiveEdit({ fieldName: field.name });
    setEditValue(String(getFieldValue(field)));
  };

  const cancelEdit = () => { setActiveEdit(null); setEditValue(""); };

  const isEditing = field => activeEdit?.fieldName === field.name;

  const renderEditableField = field => {
    const value = getFieldValue(field);
    const hasLov = field.lov && field.lov.length > 0;

    if (field.type === "READ_ONLY") {
      return (
        <div key={field.name} className="popup-custom-field">
          <span className="popup-field-label">{field.name}:</span>
          <span className="popup-field-value">{value || "—"}</span>
        </div>
      );
    }

    if (field.type === "BOOLEAN") {
      return (
        <div key={field.name} className="popup-custom-field">
          <Form.Check
            type="checkbox"
            label={field.name}
            checked={!!value}
            onChange={evt => commitBoolean(field, evt.target.checked)}
            style={{ cursor: "pointer" }}
          />
        </div>
      );
    }

    if (isEditing(field)) {
      const inputEl = hasLov ? (
        <Form.Select
          size="sm"
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => commitEdit(field)}
          onKeyDown={e => { if (e.key === "Enter") commitEdit(field); if (e.key === "Escape") cancelEdit(); }}
          style={{ flex: 1 }}
        >
          <option value="">— Select —</option>
          {field.lov.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </Form.Select>
      ) : (
        <Form.Control
          size="sm"
          ref={inputRef}
          type={field.type === "NUMBER" ? "number" : "text"}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => commitEdit(field)}
          onKeyDown={e => { if (e.key === "Enter") commitEdit(field); if (e.key === "Escape") cancelEdit(); }}
          style={{ flex: 1 }}
        />
      );
      return (
        <div key={field.name} className="popup-custom-field">
          <span className="popup-field-label">{field.name}:</span>
          {inputEl}
        </div>
      );
    }

    return (
      <div key={field.name} className="popup-custom-field">
        <span className="popup-field-label">{field.name}:</span>
        <span className="popup-field-value" style={{ color: value === "" ? "#aaa" : undefined }}>
          {value === "" ? "—" : String(value)}
        </span>
        <button
          type="button"
          className="a"
          title={`Edit ${field.name}`}
          onClick={() => startEdit(field)}
          style={{ lineHeight: 1, padding: 0, marginLeft: "0.25rem" }}
        >
          <span className="material-icons" style={{ fontSize: "0.85rem", verticalAlign: "middle", opacity: 0.5 }}>edit</span>
        </button>
      </div>
    );
  };

  const renderCustomFields = () => {
    const fields = assetGroup?.fields || [];
    if (fields.length > 0) {
      return <>{fields.map(f => renderEditableField(f))}</>;
    }
    // Fallback: read-only key inspection when no schema defined
    return utils.buildCustomFields(pinLayerPoint);
  };

  const buildUserProfileLinkDetail = (upl, idx) => {
    // console.log("buildParticipantContent", key, participant,eventParticipantGroups[key]);
    return (
      <div
        className="embedded-content-section vertical"
        style={{ padding: 0, marginBottom: "1rem" }}
      >
        <div className="participant-avatar">
          <Ratio aspectRatio="16x9">
            <UralityImage
              name={name}
              imageId={upl.coverPhotoId}
              id={`${idx}-cover-photo`}
              key={`${idx}-cover-photo`}
            />
          </Ratio>
        </div>
        <div className="participant-text" style={{ marginTop: "1rem" }}>
          <h5>{upl.displayName}</h5>
          <CollapseText text={upl.description} maxLength={200} />
        </div>
        {upl.socialProfile && (
          <SocialLinks socialProfile={upl.socialProfile} condensed={false} />
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        show={expanded}
        size={"md"}
        fullscreen={isSm}
        centered
        scrollable={true}
        onHide={() => setExpanded(false)}
      >
        <Modal.Header className="empty" closeButton>
          {/*<Modal.Title>{pinLayerPoint?.title}</Modal.Title>*/}
        </Modal.Header>
        <Modal.Body>
          {pinLayerPoint?.assets && pinLayerPoint.assets.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <Asset assets={pinLayerPoint.assets} />
            </div>
          )}
          <h4>{pinLayerPoint?.title}</h4>
          <p>
            {pinLayerPoint && pinLayerPoint.description
              ? pinLayerPoint.description
              : "No description provided."}
          </p>
          {pinLayerPoint && pinLayerPoint.location && (
            <div style={{ marginBottom: "1rem" }}>
              <MapLink
                clickable={true}
                location={pinLayerPoint.location}
                showNotes={true}
              />
            </div>
          )}
          {pinLayerPoint && pinLayerPoint.callToAction && (
            <div style={{ marginBottom: "1rem" }}>
              <a
                className="btn btn-primary"
                href={pinLayerPoint.callToAction.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {pinLayerPoint.callToAction.label}
              </a>
            </div>
          )}
          {pinLayerPoint?.userProfileLinks &&
            pinLayerPoint.userProfileLinks.length > 0 && (
              <>
                <hr />
                {pinLayerPoint.userProfileLinks.map((upl, idx) => (
                  <Fragment key={idx}>
                    {buildUserProfileLinkDetail(upl, idx)}
                    {idx < pinLayerPoint.userProfileLinks.length - 1 && <hr />}
                  </Fragment>
                ))}
              </>
            )}
          {pinLayerPoint?.activityLog && pinLayerPoint.activityLog.length > 0 && (
            <>
              <h5>Activity Log</h5>
              <div className="activity-log-view">
                {pinLayerPoint.activityLog &&
                  pinLayerPoint.activityLog.length > 0 &&
                  pinLayerPoint.activityLog.map((al, idx) => {
                    return (
                      <ActivityLogEntry
                        activityLog={al}
                        showName={false}
                        showTimestamp={true}
                      />
                    );
                  })}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
      <MapBoxMapPopup
        longitude={pinLayerPoint.longitude}
        latitude={pinLayerPoint.latitude}
        anchor="bottom"
      >
        <div className="pin-layer-point-popup">
          {pinLayerPoint.assets && pinLayerPoint.assets.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <Asset assets={pinLayerPoint.assets} />
            </div>
          )}
          <h4 style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            {pinLayerPoint.title}
            {hasOpenIncidents && (
              <Badge
                bg="danger"
                style={{ fontSize: "0.65rem", fontWeight: 500, verticalAlign: "middle" }}
              >
                Open Incident
              </Badge>
            )}
          </h4>
          <span className={"truncate-text"}>{pinLayerPoint.description}</span>
          {pinLayerPoint && pinLayerPoint.callToAction && (
            <div style={{ marginTop: "0.5rem", marginBottom: "1rem" }}>
              <a
                style={{ marginTop: "1rem" }}
                className="btn btn-primary"
                href={pinLayerPoint.callToAction.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {pinLayerPoint.callToAction.label}
              </a>
            </div>
          )}
          {(hasCustomFields(pinLayerPoint) || ctas) && (
            <div style={{ marginBottom: "1rem" }}>
              <hr />
              {editable ? renderCustomFields() : utils.buildCustomFields(pinLayerPoint)}
              {ctas && <>{ctas}</>}
            </div>
          )}
          <div>
            <button
              type="button"
              className="a"
              onClick={() => setExpanded(true)}
            >
              More Info..
            </button>
          </div>
        </div>
      </MapBoxMapPopup>
    </>
  );
};
export default PinLayerPointPopup;
